import { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import { BaseLogo } from "../../../core/models/entities/BaseLogo";
import { getActAsOrgId } from "../../../contexts/actAsStore";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { enviroment } from "../../../enviroment";
import { useManualStorageRefresh } from "../../../hooks/useManualStorageRefresh";
import {
  manualRefreshTitle,
  resolveStorageFreshnessOrgId,
} from "../../../utils/functions/storageFreshness";

interface Props {
  logo: BaseLogo;
  className?: string;
}

// The small "refresh stock now" icon button next to a storage count (logo
// list, logo detail): one click = one forced storage-freshness call for THIS
// logo, then locked for the API's 5-minute window - counted from the ROW's
// stamp, so the lock holds across tabs and reloads - with a native tooltip
// saying what happened and when it opens again (manualRefreshTitle). Flags
// storageManualRefreshEnabled + storageFreshnessEnabled both on, else nothing
// is rendered (no hook, no subscription) - the DOM stays byte-identical. An
// admin without act-as (org 0) gets no button either: there is no
// organization row to refresh.
export function StorageRefreshButton(props: Props) {
  if (
    !enviroment.storageManualRefreshEnabled ||
    !enviroment.storageFreshnessEnabled
  )
    return null;
  return <StorageRefreshButtonInner {...props} />;
}

function StorageRefreshButtonInner({ logo, className }: Props) {
  const { t } = useTranslation();
  const { role, organizationId } = useProfileContext();
  const orgId = resolveStorageFreshnessOrgId(getActAsOrgId(), role, organizationId);
  const { state, refresh } = useManualStorageRefresh(orgId, logo);
  if (!(orgId > 0)) return null;

  // Re-read on every render: the hook re-renders once a minute and on every
  // store change, and a landed answer hands the button a logo with the new
  // stamp - so the cooldown tooltip (age, minutes left) follows the stamp,
  // not the click.
  const title = manualRefreshTitle(state, logo.storageUpdatedAtUtc, Date.now(), t);
  const loading = state === "loading";
  const disabled = state !== "idle";

  // A row click on the logo list opens the detail: the click must stop here.
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    refresh();
  };

  // The tooltip sits on the wrapper: a disabled button swallows hover in
  // some browsers, so the button lets pointer events through while disabled.
  return (
    <span
      title={title}
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center ${
        disabled ? "cursor-not-allowed" : ""
      } ${className ?? ""}`}
    >
      <button
        type="button"
        aria-label={title}
        aria-busy={loading}
        data-state={state}
        disabled={disabled}
        onClick={handleClick}
        className="ml-1 p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-40 disabled:pointer-events-none"
      >
        <FontAwesomeIcon icon={faArrowsRotate} className="w-3 h-3" spin={loading} />
      </button>
    </span>
  );
}
